/** Persisted inside BusinessDraftV1 ownership (extended questionnaire JSON). */

export type StakeholderPartyType = 'individual' | 'legal_entity'

/** UI role for shareholder / director-natural-person flows */
export type StakeholderPersonRole =
  | 'beneficial_owner_and_director'
  | 'beneficial_owner'
  | 'director'

export type StakeholderIdentityType = 'id_card' | 'passport'

export interface IndividualStakeholderRow {
  partyType: StakeholderPartyType
  role: StakeholderPersonRole
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  taxCountry: string
  /** Individual tax identifier — never pre-filled from company taxId */
  taxIdPersonal: string
  residenceCountry: string
  residenceAddress: string
  identityType: StakeholderIdentityType
  identityDocumentNumber: string
  identityIssueDate: string
  identityExpiryDate: string
  /** Passport / national ID issued without an expiry date (lifetime validity) */
  identityExpiryIndefinite: boolean
  /** Percent held (string for form bindings; parsed numerically when validating) */
  percentShares: string
  legalName: string
  registrationNumber: string
  incorporationCountry: string
  incorporationAddress: string
  pepDeclaration: '' | 'yes' | 'no'
  pepDetails: string
  /** When shareholders are directors coverage is «some», this row counts as director */
  alsoDirector?: boolean
  /** Persisted onboarding person GUIDs synced from `/Onboarding/add-persons` flows */
  syncedLinks?: Partial<Record<'director' | 'ubo', string>>
}

export const STAKEHOLDER_ROLE_OPTIONS: ReadonlyArray<{ value: StakeholderPersonRole; label: string }> = [
  { value: 'beneficial_owner_and_director', label: 'Beneficial owner and director' },
  { value: 'beneficial_owner', label: 'Beneficial owner' },
  { value: 'director', label: 'Director' },
]

/** Shareholders step Role dropdown — revert when multi-role is needed again */
export const STAKEHOLDER_ROLE_OPTIONS_SHAREHOLDER_ONLY: ReadonlyArray<{
  value: StakeholderPersonRole
  label: string
}> = [{ value: 'beneficial_owner', label: 'Beneficial owner' }]

export const SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY = true

export function emptyIndividualStakeholder(): IndividualStakeholderRow {
  return {
    partyType: 'individual',
    role: 'beneficial_owner',
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    taxCountry: '',
    taxIdPersonal: '',
    residenceCountry: '',
    residenceAddress: '',
    identityType: 'passport',
    identityDocumentNumber: '',
    identityIssueDate: '',
    identityExpiryDate: '',
    identityExpiryIndefinite: false,
    percentShares: '',
    legalName: '',
    registrationNumber: '',
    incorporationCountry: '',
    incorporationAddress: '',
    pepDeclaration: '',
    pepDetails: '',
    alsoDirector: false,
  }
}

export function normalizeStakeholderRow(input: Partial<IndividualStakeholderRow> & { syncedPersonId?: string }): IndividualStakeholderRow {
  const base = emptyIndividualStakeholder()
  let role: StakeholderPersonRole = base.role
  if (input.role === 'beneficial_owner_and_director' || input.role === 'beneficial_owner' || input.role === 'director')
    role = input.role
  const partyType: StakeholderPartyType = input.partyType === 'legal_entity' ? 'legal_entity' : 'individual'
  const pepDeclaration = input.pepDeclaration === 'yes' || input.pepDeclaration === 'no' ? input.pepDeclaration : ''
  let syncedLinks = input.syncedLinks
  if (!syncedLinks && input.syncedPersonId) syncedLinks = { director: input.syncedPersonId }

  return {
    ...base,
    ...input,
    partyType,
    role,
    pepDeclaration,
    identityExpiryIndefinite: input.identityExpiryIndefinite === true,
    alsoDirector: input.alsoDirector === true,
    syncedLinks: syncedLinks ? { ...syncedLinks } : undefined,
  }
}

/** When shareholders may only carry BO role; resets director links for migrated rows */
export function clampShareholdersToBeneficialOwnerOnly(rows: IndividualStakeholderRow[]): IndividualStakeholderRow[] {
  return rows.map((r) => {
    if (r.role === 'beneficial_owner') return r
    return { ...r, role: 'beneficial_owner', syncedLinks: undefined }
  })
}

/** Parsed percent of shares — each holder must be in [0, 100]; invalid / out of range yields null */
export function parsePercentShares(raw: string): number | null {
  const n = Number(String(raw ?? '').trim().replace(',', '.'))
  if (!Number.isFinite(n) || n < 0 || n > 100) return null
  return n
}

export function shareholderPercentTotal(rows: IndividualStakeholderRow[]): number | null {
  let sum = 0
  for (const r of rows) {
    const p = parsePercentShares(r.percentShares)
    if (p === null) return null
    sum += p
  }
  return sum
}

export function isDirectorEligibleRole(role: StakeholderPersonRole): boolean {
  return role === 'beneficial_owner_and_director' || role === 'director'
}

/** Shareholder counts as company director via role or via «shareholders are directors» questionnaire wiring */
export function shareholderRowCountsAsDirector(
  row: IndividualStakeholderRow,
  ownership: {
    shareholdersAreDirectors?: 'yes' | 'no' | ''
    shareholdersDirectorCoverage?: '' | 'all' | 'some'
  }
): boolean {
  if (isDirectorEligibleRole(row.role)) return true
  const adr = ownership.shareholdersAreDirectors
  const cov = ownership.shareholdersDirectorCoverage ?? ''
  if (adr !== 'yes') return false
  if (cov !== 'all' && cov !== 'some') return false
  if (cov === 'all') return true
  return row.alsoDirector === true
}

export function shareholderCompositeName(r: IndividualStakeholderRow): string {
  if (r.partyType === 'legal_entity') return String(r.legalName ?? '').trim()
  return [r.lastName, r.firstName, r.middleName].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ')
}

export function formatPersonApiFromStakeholder(r: IndividualStakeholderRow): {
  fullName: string
  nationality: string
  idNumber: string
  partyType: StakeholderPartyType
  firstName?: string | null
  lastName?: string | null
  middleName?: string | null
  dateOfBirth?: string | null
  residenceCountry?: string | null
  residenceAddress?: string | null
  legalName?: string | null
  registrationNumber?: string | null
  incorporationCountry?: string | null
  incorporationAddress?: string | null
  ownershipPercentage?: number | null
  pepDeclaration?: boolean | null
  pepDetails?: string | null
} {
  const ownershipPercentage = parsePercentShares(r.percentShares)
  const isLegalEntity = r.partyType === 'legal_entity'
  return {
    fullName: shareholderCompositeName(r) || [r.firstName, r.lastName].join(' ').trim(),
    nationality: isLegalEntity
      ? String(r.incorporationCountry ?? '').trim() || '—'
      : String(r.residenceCountry ?? '').trim() || String(r.taxCountry ?? '').trim() || '—',
    idNumber: isLegalEntity
      ? String(r.registrationNumber ?? '').trim() || '—'
      : String(r.identityDocumentNumber ?? '').trim() || '—',
    partyType: r.partyType,
    firstName: isLegalEntity ? null : String(r.firstName ?? '').trim() || null,
    lastName: isLegalEntity ? null : String(r.lastName ?? '').trim() || null,
    middleName: isLegalEntity ? null : String(r.middleName ?? '').trim() || null,
    dateOfBirth: isLegalEntity ? null : String(r.dateOfBirth ?? '').trim() || null,
    residenceCountry: isLegalEntity ? null : String(r.residenceCountry ?? '').trim() || null,
    residenceAddress: isLegalEntity ? null : String(r.residenceAddress ?? '').trim() || null,
    legalName: isLegalEntity ? String(r.legalName ?? '').trim() || null : null,
    registrationNumber: isLegalEntity ? String(r.registrationNumber ?? '').trim() || null : null,
    incorporationCountry: isLegalEntity ? String(r.incorporationCountry ?? '').trim() || null : null,
    incorporationAddress: isLegalEntity ? String(r.incorporationAddress ?? '').trim() || null : null,
    ownershipPercentage,
    pepDeclaration: r.pepDeclaration === '' ? null : r.pepDeclaration === 'yes',
    pepDetails: r.pepDeclaration === 'yes' ? String(r.pepDetails ?? '').trim() || null : null,
  }
}

export function validateIndividualStakeholder(
  r: IndividualStakeholderRow,
  opts: { requirePercent: boolean }
): string | undefined {
  if (r.partyType === 'legal_entity') {
    if (!String(r.legalName ?? '').trim()) return 'Legal entity name is required.'
    if (!String(r.registrationNumber ?? '').trim()) return 'Registration number is required.'
    if (!String(r.incorporationCountry ?? '').trim()) return 'Country of incorporation is required.'
    if (!String(r.incorporationAddress ?? '').trim()) return 'Incorporation address is required.'
  } else {
    if (!String(r.firstName ?? '').trim()) return 'Legal first name is required.'
    if (!String(r.lastName ?? '').trim()) return 'Legal last name is required.'
    if (!String(r.dateOfBirth ?? '').trim()) return 'Date of birth is required.'
    if (!String(r.taxCountry ?? '').trim()) return 'Tax country is required.'
    if (!String(r.taxIdPersonal ?? '').trim())
      return 'Personal tax ID is required (do not use the company registration / tax ID).'
    if (!String(r.residenceCountry ?? '').trim()) return 'Residence country is required.'
    if (!String(r.residenceAddress ?? '').trim()) return 'Residence address is required.'
    if (!String(r.identityDocumentNumber ?? '').trim()) return 'Identity document number is required.'
    if (!String(r.identityIssueDate ?? '').trim()) return 'Identity document issue date is required.'
    if (
      !(r.identityExpiryIndefinite === true) &&
      !String(r.identityExpiryDate ?? '').trim()
    )
      return 'Identity document expiry date is required, or tick “No expiry date”.'
  }
  const raw = String(r.percentShares ?? '').trim()
  if (raw !== '') {
    const n = Number(raw.replace(',', '.'))
    if (!Number.isFinite(n)) return 'Ownership % must be a number.'
    if (n < 0 || n > 100) return 'Ownership % must be between 0 and 100.'
  }
  if (opts.requirePercent && raw === '') return 'Ownership % is required.'
  if (r.pepDeclaration !== 'yes' && r.pepDeclaration !== 'no') return 'PEP declaration is required.'
  if (r.pepDeclaration === 'yes' && !String(r.pepDetails ?? '').trim()) return 'PEP details are required.'
  return undefined
}

export function shouldValidateShareholdersTotal100(rows: IndividualStakeholderRow[]): boolean {
  return rows.length > 0
}

const OWNER_TOTAL_EPSILON = 0.01

export function isShareholderOwnershipTotalValid(rows: IndividualStakeholderRow[]): boolean {
  const t = shareholderPercentTotal(rows)
  if (t === null) return false
  return Math.abs(t - 100) <= OWNER_TOTAL_EPSILON
}
